import { isNil } from 'lodash';
import { useCallback, useContext } from 'react';
import { RelatedEntity } from '@shared/data/entity/fields/RelatedEntityField';
import {
  CurrentCardNavigationItemContext,
  useCardNavigationActions,
} from '@shared/scenes/cards/navigation';
import { useGetChannel } from '@shared/scenes/channel/hooks';
import { i18n } from '@shared/util/i18n';
import { useChannelSlug, useNavigator } from '@shared/util/navigation';

/**
 * Opens a related card in its channel. From inside another card (`currentEntityId`), it also adds
 * a back step to return there.
 */
export function useOpenRelatedEntity(currentEntityId?: string) {
  const currentChannelSlug = useChannelSlug();
  const { navigate } = useNavigator();
  const getChannel = useGetChannel();
  const { addStackItem, addStackItemFrom } = useCardNavigationActions();
  // The card update form this field is in, including from a subtable row's form on that card.
  const currentCard = useContext(CurrentCardNavigationItemContext);

  return useCallback(
    async (entity: RelatedEntity) => {
      const { channel } = await getChannel(entity.channelId);

      if (!channel) {
        return;
      }

      const item = {
        entityId: entity.id,
        channelSlug: channel.slug,
        backTooltip: i18n('card.navigation.returnToRelatedCard'),
      };
      if (!isNil(currentCard)) {
        addStackItemFrom(currentCard, item);
      } else if (currentEntityId && currentChannelSlug) {
        addStackItem(item);
      }

      navigate('CardUpdate', { cardId: entity.id, channelSlug: channel.slug });
    },
    [
      addStackItem,
      addStackItemFrom,
      currentCard,
      getChannel,
      navigate,
      currentEntityId,
      currentChannelSlug,
    ],
  );
}
